/**
 * GOVERNANCE OPERATIONAL VISUAL LANGUAGE V1 — Timeline Semantics
 *
 * Visual contracts for timeline lane layout, continuity transitions,
 * refusal overlays, replay window overlays, GHA workflow hierarchy,
 * and governance span nesting.
 *
 * DOCTRINE:
 * The timeline is the primary operational surface. It is NOT a metrics chart.
 * It renders governance events as positioned moments on semantic lanes.
 * Lane identity is governance-native, not metric-native.
 *
 * Lane hierarchy (top → bottom):
 *   Admissibility → Executions → Refusals → Continuity → Boundaries → Replays
 *
 * GHA sub-hierarchy within Executions:
 *   Workflow run → Job → Step
 *
 * Continuity lane renders the state line — a continuous color-coded
 * horizontal band showing stable/degraded/interrupted/unresolved/stabilized.
 *
 * Refusal lane: vertical red tick marks, NOT block fills.
 * Replay lane: blue tint overlay bands for window spans.
 *
 * INVARIANTS:
 * - Lane assignments are pure typed data. No rendering logic.
 * - Lane constants are numeric (px). Rendering layer applies units.
 * - GHA hierarchy uses fixed pixel indentation per level.
 */

import { TIMELINE_LANE, ROW_HEIGHT } from './tokens.js';
import type { ContinuityStateMarker } from '../operationalRendering/types.js';
import { SEMANTIC, TIMELINE_OVERLAY } from './colorSemantics.js';

// ---------------------------------------------------------------------------
// Timeline lane definitions
// ---------------------------------------------------------------------------

/**
 * TimelineLaneDef — definition of one semantic lane in the timeline.
 *
 * Lanes are ordered top-to-bottom by their laneOrder.
 * Rule: All lanes have a fixed height. The continuity lane may be taller.
 * Rule: Lane labels are SHORT — max 12 chars.
 */
export interface TimelineLaneDef {
  laneId: string;
  label: string;
  laneOrder: number;
  heightPx: number;
  /** Lane is always visible regardless of whether it has events */
  alwaysVisible: boolean;
  /** Color of the lane label text (CSS color string) */
  labelColor: string;
  /** Background stripe color (very subtle) */
  bgColor: string;
  /** Separator color below this lane */
  separatorColor: string;
}

export const TIMELINE_LANES: TimelineLaneDef[] = [
  {
    laneId: 'admissibility',
    label: 'Admissibility',
    laneOrder: 0,
    heightPx: ROW_HEIGHT.compact.timeline,
    alwaysVisible: true,
    labelColor: SEMANTIC['admissible'].text,
    bgColor: 'transparent',
    separatorColor: '#1e1e23',
  },
  {
    laneId: 'executions',
    label: 'Executions',
    laneOrder: 1,
    heightPx: ROW_HEIGHT.compact.timeline,
    alwaysVisible: true,
    labelColor: '#6080a8',
    bgColor: 'transparent',
    separatorColor: '#1e1e23',
  },
  {
    laneId: 'refusals',
    label: 'Refusals',
    laneOrder: 2,
    heightPx: ROW_HEIGHT.compact.timeline,
    alwaysVisible: true,
    labelColor: SEMANTIC['refusal'].text,
    bgColor: 'transparent',
    separatorColor: '#1e1e23',
  },
  {
    laneId: 'continuity',
    label: 'Continuity',
    laneOrder: 3,
    heightPx: ROW_HEIGHT.compact.timeline + 4,
    alwaysVisible: true,
    labelColor: '#c0c0d0',
    bgColor: 'transparent',
    separatorColor: '#1e1e23',
  },
  {
    laneId: 'boundaries',
    label: 'Boundaries',
    laneOrder: 4,
    heightPx: ROW_HEIGHT.compact.timeline,
    alwaysVisible: false,
    labelColor: SEMANTIC['boundaryViolation'].text,
    bgColor: 'transparent',
    separatorColor: '#1e1e23',
  },
  {
    laneId: 'replays',
    label: 'Replays',
    laneOrder: 5,
    heightPx: ROW_HEIGHT.compact.timeline,
    alwaysVisible: false,
    labelColor: SEMANTIC['replayWindow'].text,
    bgColor: 'transparent',
    separatorColor: 'transparent',
  },
] as const;

// ---------------------------------------------------------------------------
// Event → lane mapping
// ---------------------------------------------------------------------------

/**
 * GOVERNANCE_EVENT_LANE_MAP — maps GovernanceEventType groups to lane IDs.
 *
 * Rule: GHA events map to 'executions'.
 * Rule: REFUSAL maps to 'refusals'.
 * Rule: CONTINUITY_TRANSITION maps to 'continuity'.
 * Rule: BOUNDARY_VIOLATION and EXECUTION_GATED map to 'admissibility'.
 * Rule: Replay events map to 'replays'.
 */
export const GOVERNANCE_EVENT_LANE_MAP: Record<string, string> = {
  STATE_TRANSITION: 'admissibility',
  REFUSAL: 'refusals',
  LIFECYCLE_STAGE: 'executions',
  EXECUTION_GATED: 'admissibility',
  BOUNDARY_VIOLATION: 'boundaries',
  RATCHET_STEP: 'executions',
  GOVERNANCE_DECISION: 'admissibility',
  EVOLUTION_TRACE_STEP: 'executions',
  LOOP_ITERATION: 'executions',
  LOOP_TERMINAL: 'executions',
  CONTINUITY_TRANSITION: 'continuity',
  REPLAY_WINDOW: 'replays',
  GHA_WORKFLOW_RUN_COMPLETED: 'executions',
  GHA_JOB_COMPLETED: 'executions',
  GHA_STEP_COMPLETED: 'executions',
  GHA_DEPLOY_GATE_EVALUATED: 'admissibility',
  GHA_PR_GATE_EVALUATED: 'admissibility',
  GHA_EVOLUTION_CANDIDATE_REGISTERED: 'executions',
  GHA_EPOCH_VERIFIED: 'executions',
  GHA_APPROVAL_GATE_EVALUATED: 'admissibility',
  GHA_GOVERNANCE_OUTPUT_EMITTED: 'executions',
} as const;

/**
 * Derive the lane ID for a GovernanceEventType.
 * Pure function. Deterministic. Falls back to 'executions'.
 */
export function deriveEventLane(eventType: string): string {
  return GOVERNANCE_EVENT_LANE_MAP[eventType] ?? 'executions';
}

// ---------------------------------------------------------------------------
// Continuity state line
// ---------------------------------------------------------------------------

/**
 * ContinuityStateLine — a segment of the continuity lane.
 *
 * The continuity lane renders a horizontal color band.
 * Each segment spans from startTimestamp to endTimestamp in one ContinuityStateMarker color.
 *
 * Rule: Segment color is the marker color from colorSemantics.
 * Rule: Segments are butted together — no gap between them.
 * Rule: Transition points are marked with a vertical tick (continuityTransitionDot).
 */
export interface ContinuityStateLine {
  transitionId: string;
  startTimestamp: string;
  endTimestamp: string | null;
  status: ContinuityStateMarker;
  color: string;
  isOpen: boolean;
}

const CONTINUITY_STATUS_MARKER_COLORS: Record<ContinuityStateMarker, string> = {
  stable:      SEMANTIC['stable'].marker,
  degraded:    SEMANTIC['degraded'].marker,
  interrupted: SEMANTIC['interrupted'].marker,
  unresolved:  SEMANTIC['unresolved'].marker,
  stabilized:  SEMANTIC['stabilized'].marker,
  unknown:     SEMANTIC['unknown'].marker,
} as const;

/**
 * Build ContinuityStateLine segments from an ordered list of continuity entries.
 * Pure function. Deterministic.
 *
 * The last segment is open (endTimestamp = null) if no subsequent transition.
 */
export function buildContinuityStateLines(
  entries: Array<{
    transitionId: string;
    timestamp: string;
    currentStatus: ContinuityStateMarker;
  }>
): ContinuityStateLine[] {
  const lines: ContinuityStateLine[] = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const next = entries[i + 1];
    lines.push({
      transitionId: entry.transitionId,
      startTimestamp: entry.timestamp,
      endTimestamp: next?.timestamp ?? null,
      status: entry.currentStatus,
      color: CONTINUITY_STATUS_MARKER_COLORS[entry.currentStatus] ?? SEMANTIC['unknown'].marker,
      isOpen: !next,
    });
  }
  return lines;
}

// ---------------------------------------------------------------------------
// Refusal overlay marker
// ---------------------------------------------------------------------------

/**
 * RefusalOverlayMarker — a vertical red tick on the refusal lane.
 *
 * Rule: Width is TIMELINE_LANE.refusalOverlayWidth (2px) — NOT a block fill.
 * Rule: Color is always TIMELINE_OVERLAY.refusalOverlay.
 * Rule: Height spans the full timeline height.
 * Rule: Multiple refusals at close timestamps are clustered, not stacked.
 */
export interface RefusalOverlayMarker {
  eventId: string;
  timestamp: string;
  color: string;
  widthPx: number;
  tooltipLabel: string;
}

export function buildRefusalOverlayMarker(
  eventId: string,
  timestamp: string,
  label: string
): RefusalOverlayMarker {
  return {
    eventId,
    timestamp,
    color: TIMELINE_OVERLAY.refusalOverlay,
    widthPx: TIMELINE_LANE.refusalOverlayWidth,
    tooltipLabel: label,
  };
}

// ---------------------------------------------------------------------------
// Replay window overlay
// ---------------------------------------------------------------------------

/**
 * ReplayWindowOverlay — a tinted horizontal band on the timeline.
 *
 * Rule: Opacity is TIMELINE_LANE.replayWindowOverlayOpacity.
 * Rule: Border is replayWindowBorder from TIMELINE_OVERLAY.
 * Rule: Rendered BELOW event markers — events on top of tint.
 */
export interface ReplayWindowOverlay {
  windowId: string;
  startTimestamp: string;
  endTimestamp: string;
  bgColor: string;
  borderColor: string;
  opacity: number;
}

export function buildReplayWindowOverlay(
  windowId: string,
  startTimestamp: string,
  endTimestamp: string
): ReplayWindowOverlay {
  return {
    windowId,
    startTimestamp,
    endTimestamp,
    bgColor: TIMELINE_OVERLAY.replayWindowBg,
    borderColor: TIMELINE_OVERLAY.replayWindowBorder,
    opacity: TIMELINE_LANE.replayWindowOverlayOpacity,
  };
}

// ---------------------------------------------------------------------------
// GHA workflow hierarchy in timeline
// ---------------------------------------------------------------------------

/**
 * GhaTimelineHierarchyLevel — visual nesting level for GHA events.
 *
 * Rule: Workflow run → depth 0, Job → depth 1, Step → depth 2.
 * Rule: Each depth indents by TIMELINE_LANE.ghaDepthIndent px.
 * Rule: Sub-events are shown as smaller markers.
 */
export interface GhaTimelineHierarchyLevel {
  eventType: string;
  depth: 0 | 1 | 2;
  markerDiameter: number;
  indentPx: number;
}

export const GHA_HIERARCHY_LEVELS: GhaTimelineHierarchyLevel[] = [
  {
    eventType: 'GHA_WORKFLOW_RUN_COMPLETED',
    depth: 0,
    markerDiameter: TIMELINE_LANE.markerDiameter,
    indentPx: 0,
  },
  {
    eventType: 'GHA_JOB_COMPLETED',
    depth: 1,
    markerDiameter: TIMELINE_LANE.markerDiameter - 2,
    indentPx: TIMELINE_LANE.ghaDepthIndent,
  },
  {
    eventType: 'GHA_STEP_COMPLETED',
    depth: 2,
    markerDiameter: TIMELINE_LANE.markerDiameter - 3,
    indentPx: TIMELINE_LANE.ghaDepthIndent * 2,
  },
  {
    eventType: 'GHA_PR_GATE_EVALUATED',
    depth: 1,
    markerDiameter: TIMELINE_LANE.markerDiameter,
    indentPx: TIMELINE_LANE.ghaDepthIndent,
  },
  {
    eventType: 'GHA_DEPLOY_GATE_EVALUATED',
    depth: 1,
    markerDiameter: TIMELINE_LANE.markerDiameter,
    indentPx: TIMELINE_LANE.ghaDepthIndent,
  },
  {
    eventType: 'GHA_APPROVAL_GATE_EVALUATED',
    depth: 1,
    markerDiameter: TIMELINE_LANE.markerDiameter,
    indentPx: TIMELINE_LANE.ghaDepthIndent,
  },
  {
    eventType: 'GHA_EVOLUTION_CANDIDATE_REGISTERED',
    depth: 0,
    markerDiameter: TIMELINE_LANE.markerDiameter,
    indentPx: 0,
  },
  {
    eventType: 'GHA_EPOCH_VERIFIED',
    depth: 0,
    markerDiameter: TIMELINE_LANE.markerDiameter,
    indentPx: 0,
  },
  {
    eventType: 'GHA_GOVERNANCE_OUTPUT_EMITTED',
    depth: 2,
    markerDiameter: TIMELINE_LANE.markerDiameter - 2,
    indentPx: TIMELINE_LANE.ghaDepthIndent * 2,
  },
] as const;

/**
 * Derive the GHA hierarchy level for an eventType.
 * Pure function. Falls back to depth=0 for unknown GHA types.
 */
export function deriveGhaHierarchyLevel(
  eventType: string
): GhaTimelineHierarchyLevel {
  return GHA_HIERARCHY_LEVELS.find(l => l.eventType === eventType) ?? {
    eventType,
    depth: 0,
    markerDiameter: TIMELINE_LANE.markerDiameter,
    indentPx: 0,
  };
}

// ---------------------------------------------------------------------------
// Cursor line
// ---------------------------------------------------------------------------

/**
 * TimelineCursorLine — the vertical cursor line showing selected time.
 *
 * Rule: Single cursor line. Spans all lanes.
 * Rule: Color is TIMELINE_OVERLAY.cursorLine.
 * Rule: Width is 1px. NOT a range bar.
 */
export interface TimelineCursorLine {
  timestamp: string;
  color: string;
  widthPx: number;
  opacity: number;
}

export function buildTimelineCursorLine(timestamp: string): TimelineCursorLine {
  return {
    timestamp,
    color: TIMELINE_OVERLAY.cursorLine,
    widthPx: 1,
    opacity: 0.8,
  };
}

// ---------------------------------------------------------------------------
// Governance span bracket
// ---------------------------------------------------------------------------

/**
 * GovernanceSpanBracket — a vertical bracket on the left edge of the timeline
 * indicating a GovernanceSpan's temporal extent.
 *
 * Rule: Width is TIMELINE_LANE.spanBracketWidth (2px).
 * Rule: Opacity is TIMELINE_LANE.spanBracketOpacity.
 * Rule: Color derived from span status: COMPLETED=stable, FAILED=refusal,
 *       BLOCKED=boundaryViolation, OPEN=informational, UNKNOWN=unknown.
 */
export interface GovernanceSpanBracket {
  spanId: string;
  startTimestamp: string;
  endTimestamp: string | null;
  color: string;
  widthPx: number;
  opacity: number;
  tooltipLabel: string;
}

export function buildGovernanceSpanBracket(
  spanId: string,
  startTimestamp: string,
  endTimestamp: string | null,
  status: string
): GovernanceSpanBracket {
  const colorMap: Record<string, string> = {
    COMPLETED: SEMANTIC['stable'].marker,
    FAILED: SEMANTIC['refusal'].marker,
    BLOCKED: SEMANTIC['boundaryViolation'].marker,
    OPEN: SEMANTIC['informational'].marker,
    UNKNOWN: SEMANTIC['unknown'].marker,
  };
  return {
    spanId,
    startTimestamp,
    endTimestamp,
    color: colorMap[status] ?? SEMANTIC['unknown'].marker,
    widthPx: TIMELINE_LANE.spanBracketWidth,
    opacity: TIMELINE_LANE.spanBracketOpacity,
    tooltipLabel: `Span: ${spanId.slice(0, 12)}… [${status}]`,
  };
}

// ---------------------------------------------------------------------------
// Proof boundary marker
// ---------------------------------------------------------------------------

/**
 * ProofBoundaryMarker — a visual indicator on the timeline that a
 * proofOnly boundary is in effect for a region.
 *
 * Rule: Rendered as a diagonal-stripe tint over the affected time range.
 * Rule: Color is TIMELINE_OVERLAY.proofOnlyStripe.
 * Rule: Never blocks event markers from being visible.
 */
export interface ProofBoundaryMarker {
  regionId: string;
  startTimestamp: string;
  endTimestamp: string;
  stripeBg: string;
  tooltipLabel: string;
}

export function buildProofBoundaryMarker(
  regionId: string,
  startTimestamp: string,
  endTimestamp: string
): ProofBoundaryMarker {
  return {
    regionId,
    startTimestamp,
    endTimestamp,
    stripeBg: TIMELINE_OVERLAY.proofOnlyStripe,
    tooltipLabel: 'Proof-only region — restricted visibility',
  };
}

// ---------------------------------------------------------------------------
// Show continuity toggle options
// ---------------------------------------------------------------------------

/**
 * ContinuityOverlayOption — the options available in the "Show Continuity" toggle.
 *
 * Mirrors the UI control from the reference image.
 */
export type ContinuityOverlayOption = 'state' | 'transitions' | 'none';

export interface ContinuityOverlayRule {
  option: ContinuityOverlayOption;
  showStateLine: boolean;
  showTransitionDots: boolean;
  showRefusalEvents: boolean;
}

export const CONTINUITY_OVERLAY_RULES: Record<ContinuityOverlayOption, ContinuityOverlayRule> = {
  state: {
    option: 'state',
    showStateLine: true,
    showTransitionDots: true,
    showRefusalEvents: false,
  },
  transitions: {
    option: 'transitions',
    showStateLine: false,
    showTransitionDots: true,
    showRefusalEvents: true,
  },
  none: {
    option: 'none',
    showStateLine: false,
    showTransitionDots: false,
    showRefusalEvents: false,
  },
} as const;
